// Rentals and sponsorships in Hub, Phase 1 (ADR 0041; the rentals and
// sponsorships plan, docs/architecture/BRE-Rentals-and-Sponsorships-Plan.md).
//
// Booking stays the system of record and the engine (owner, 2026-10-14,
// model A). Hub reads it, read-only, through contract v1 under Booking's
// /api/internal/hub/v1, behind Booking's HUB_READ_SECRET:
//
//   GET /rentals?cursor=<id>&limit=<1..100>        pipeline rows, by id; a full read is every page
//   GET /rentals?ids=<id>,<id>                     those rows, and `missing` for ids Booking no longer has
//   GET /rentals/<id>                              one rental in full
//   GET /sponsorships?...  /sponsorships/<id>      the same for sponsorships
//   GET /changes?cursor=<seq>&limit=<1..500>       Booking's change feed (its hub_changes outbox, 0108)
//   GET /contacts?cursor=<id>&limit=<1..200>       every contact, with how Booking linked it to a BRE account
//
// Same auth, headers and rules as source-read.ts. Money is Booking's own, in
// integer cents: never QuickBooks, and labeled as Booking's wherever it is
// shown. A rental with nothing billed has a null balance ("not billed"),
// never zero. Times are ISO 8601 UTC; business dates are venue-local
// (America/Chicago) YYYY-MM-DD.
//
// Never in any of these: signer IPs, user agents, hashes or names, payment
// methods or card and bank details, Stripe or QuickBooks ids, file keys,
// signing or portal tokens, internal notes, COI policy numbers. Contact
// emails and phones arrive only in /contacts, as evidence: Hub links a
// contact to a person by BRE Auth subject alone, and only when Booking's
// link to that subject was proven (decision 8, contactLinkDecision below).
//
// The schemas are parsed with zod's default (unknown keys dropped), so
// anything else Booking might add never reaches Hub's database.

import { z } from 'zod'
import { addDays, daysBetween } from './finance-hub'

export const BOOKING_RECORDS_SOURCE = 'booking'
export const BOOKING_RENTALS_PATH = '/rentals'
export const BOOKING_SPONSORSHIPS_PATH = '/sponsorships'
export const BOOKING_CHANGES_PATH = '/changes'
export const BOOKING_CONTACTS_PATH = '/contacts'
export const BOOKING_RECORDS_PAGE_SIZE = 50
export const BOOKING_RECORDS_MAX_LIMIT = 100
export const BOOKING_RECORDS_MAX_IDS = 100
export const BOOKING_CHANGES_PAGE_SIZE = 200
export const BOOKING_CHANGES_MAX_LIMIT = 500
export const BOOKING_CONTACTS_PAGE_SIZE = 100
export const BOOKING_CONTACTS_MAX_LIMIT = 200

export type BookingRecordKind = 'rental' | 'sponsorship'
export const BOOKING_RECORD_KINDS: BookingRecordKind[] = ['rental', 'sponsorship']
export const isBookingRecordKind = (v: unknown): v is BookingRecordKind => v === 'rental' || v === 'sponsorship'

// ------------------------------------------------------------ the shapes

const Id = z.string().min(1).max(64)
const Text = (max: number) => z.string().max(max)
const NText = (max: number) => z.string().max(max).nullable()
const Iso = z.string().max(40)
const NIso = Iso.nullable()
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const NDay = Day.nullable()
const Cents = z.number().int().refine(Number.isSafeInteger)
const NCents = Cents.nullable()
const Count = z.number().int().min(0)
const Url = z.string().max(1000).regex(/^https?:\/\//i).nullable()

const Party = {
  owner: z.object({ sub: NText(200), name: NText(200) }).nullable(),
  organization: z.object({ sourceId: Id, name: NText(500) }).nullable(),
  contact: z.object({ sourceId: Id, name: NText(500), linkedToBreAccount: z.boolean() }).nullable(),
}

const InvoiceMoney = {
  billedMinor: Cents,
  collectedMinor: Cents,
  // Null when nothing has been billed.
  balanceMinor: NCents,
  overdueMinor: Cents,
  // An ACH or other bank payment in flight.
  pendingMinor: Cents,
  unbilledInvoices: Count,
  nextDueDate: NDay,
  currency: Text(3),
}

export const BookingRentalRow = z.object({
  sourceId: Id,
  kind: z.literal('rental'),
  // Booking's own word: inquiry, estimate, contract, confirmed, completed, cancelled.
  status: Text(40),
  title: Text(500),
  eventType: NText(200),
  rentalType: z.enum(['standard', 'ticketed']),
  // The renter as Booking typed it on the rental: evidence, never a link.
  client: z.object({ name: NText(500), organization: NText(500) }),
  ...Party,
  startsAt: NIso,
  endsAt: NIso,
  eventDate: NDay,
  lastDate: NDay,
  venue: NText(500),
  rooms: z.array(z.object({ sourceId: Id, name: Text(500) })).max(100),
  expectedAttendance: z.number().int().nullable(),
  holds: z.object({ active: Count, bestLevel: z.number().int().nullable(), expiresAt: NIso }),
  money: z.object({ totalMinor: NCents, depositMinor: NCents, depositDueDate: NDay, taxed: z.boolean(), ...InvoiceMoney }),
  paperwork: z.object({
    // draft, sent, client_signed, fully_signed; null when no contract yet.
    contract: NText(40),
    contractSignedAt: NIso,
    countersignedAt: NIso,
    packetsOpen: Count,
    packetsToCountersign: Count,
    // not_required, required, submitted, verified, rejected, expired.
    coi: NText(40),
    // Vertical Insure offer: not_offered, purchased, declined_no_coverage, purchase_failed.
    insurance: NText(40),
    // The latest policy: quoted, bound, payment_failed, canceled.
    policy: NText(40),
    documentRequestsOpen: Count,
  }),
  // pending, in_review, closed; null when Booking has no closeout for it.
  closeout: NText(40),
  // Booking's portal invite. myBRE replaces it in Phase 3.
  portal: z.object({ invitedAt: NIso }),
  // Booking's feed position for this record: it goes up whenever the rental
  // or anything belonging to it changes. Hub's commands (Phase 2) send it
  // back as `expected`.
  version: z.string().regex(/^\d{1,15}$/),
  createdAt: NIso,
  updatedAt: NIso,
  url: Url,
})
export type BookingRentalRow = z.infer<typeof BookingRentalRow>

const DeliverableCounts = z.object({ total: Count, fulfilled: Count, awaitingReview: Count, needsRevision: Count, overdue: Count })

export const BookingSponsorshipRow = z.object({
  sourceId: Id,
  kind: z.literal('sponsorship'),
  // prospect, proposal, agreement, invoiced, paid, completed, cancelled.
  status: Text(40),
  title: Text(500),
  packageName: NText(500),
  eventName: NText(500),
  breEvent: z.object({ sourceId: Id, name: NText(500), date: NDay }).nullable(),
  tierName: NText(200),
  compensation: z.enum(['cash', 'trade', 'cash_and_trade']),
  billingCadence: z.enum(['annual', 'monthly']),
  ...Party,
  startDate: NDay,
  endDate: NDay,
  renewal: z.object({ type: NText(40), status: NText(40), date: NDay, windowStart: NDay, windowEnd: NDay }),
  agreement: z.object({ status: NText(40), sentAt: NIso, signedAt: NIso }),
  itemCount: Count,
  deliverables: DeliverableCounts,
  money: z.object({ amountMinor: NCents, tradeValueMinor: NCents, ...InvoiceMoney }),
  version: z.string().regex(/^\d{1,15}$/),
  createdAt: NIso,
  updatedAt: NIso,
  url: Url,
})
export type BookingSponsorshipRow = z.infer<typeof BookingSponsorshipRow>
export type BookingRecordRow = BookingRentalRow | BookingSponsorshipRow

const pageOf = <T extends z.ZodTypeAny>(row: T) =>
  z.object({
    version: z.literal(1),
    asOf: Iso,
    records: z.array(row).max(BOOKING_RECORDS_MAX_IDS),
    missing: z.array(Id).max(BOOKING_RECORDS_MAX_IDS),
    nextCursor: z.string().max(64).nullable(),
    hasMore: z.boolean(),
  })
export const BookingRentalPage = pageOf(BookingRentalRow)
export const BookingSponsorshipPage = pageOf(BookingSponsorshipRow)
export type BookingRentalPage = z.infer<typeof BookingRentalPage>
export type BookingSponsorshipPage = z.infer<typeof BookingSponsorshipPage>

const Invoice = z.object({
  sourceId: Id,
  number: NText(60),
  // draft, scheduled, sent, partial, paid, overdue, voided.
  status: Text(40),
  totalMinor: Cents,
  paidMinor: Cents,
  // Null for an invoice not yet billed (draft, scheduled).
  balanceMinor: NCents,
  issuedDate: NDay,
  dueDate: NDay,
  pendingPayment: NText(40),
  collection: NText(40),
  url: Url,
})
const Payment = z.object({
  sourceId: Id,
  invoiceId: Id,
  amountMinor: Cents,
  refundedMinor: Cents,
  // A word only: card, bank, check, cash, wire, other.
  method: Text(20),
  status: Text(40),
  paidAt: NIso,
  createdAt: NIso,
})
const Message = z.object({ sourceId: Id, from: z.enum(['customer', 'bre']), authorName: NText(200), body: Text(20000), at: NIso })
const Activity = z.object({
  at: NIso,
  action: Text(100),
  title: Text(1000),
  entity: Text(40),
  actor: z.object({ name: NText(200), staff: z.boolean() }),
  visibleToCustomer: z.boolean(),
})

export const BookingRentalDetail = z.object({
  version: z.literal(1),
  asOf: Iso,
  record: BookingRentalRow,
  spaces: z
    .array(
      z.object({
        sourceId: Id,
        roomId: Id,
        roomName: NText(500),
        venue: NText(500),
        segment: z.enum(['event', 'setup', 'teardown']),
        startsAt: NIso,
        endsAt: NIso,
        eventStartsAt: NIso,
        eventEndsAt: NIso,
        // As Booking reports it to Hub's calendar (ADR 0023): never availability.
        status: z.enum(['tentative', 'confirmed', 'canceled']),
      }),
    )
    .max(200),
  dates: z.array(z.object({ date: Day, type: Text(40), startTime: NText(10), endTime: NText(10) })).max(200),
  holds: z.array(z.object({ sourceId: Id, date: NDay, level: z.number().int(), roomId: NText(64), roomName: NText(500), status: Text(40), expiresAt: NIso })).max(200),
  quote: z.object({ lines: z.array(z.object({ description: Text(2000), quantity: z.number(), unitMinor: Cents, totalMinor: Cents, category: NText(200) })).max(200) }),
  invoices: z.array(Invoice).max(200),
  payments: z.array(Payment).max(200),
  contract: z.object({
    status: NText(40),
    clientSignedAt: NIso,
    countersignedAt: NIso,
    packets: z.array(z.object({ sourceId: Id, title: Text(500), status: Text(40), sentAt: NIso, clientSignedAt: NIso, countersignedAt: NIso })).max(200),
  }),
  insurance: z.object({
    coi: NText(40),
    offer: NText(40),
    policies: z.array(z.object({ status: Text(40), coverageMinor: NCents, premiumMinor: NCents, effectiveDate: NDay, expirationDate: NDay, boundAt: NIso })).max(200),
  }),
  documents: z
    .array(z.object({ sourceId: Id, name: Text(500), uploadedBy: z.enum(['renter', 'bre']), ackRequired: z.boolean(), acknowledgedAt: NIso, mimeType: NText(200), sizeBytes: z.number().int().nullable(), createdAt: NIso }))
    .max(200),
  documentRequests: z.array(z.object({ sourceId: Id, title: Text(500), status: Text(40), dueDate: NDay, createdAt: NIso })).max(200),
  documentsUrl: Url,
  messages: z.array(Message).max(200),
  activity: z.array(Activity).max(200),
})
export type BookingRentalDetail = z.infer<typeof BookingRentalDetail>

const Deliverable = z.object({ description: Text(2000), fulfilled: z.boolean(), dueDate: NDay, sponsorStatus: NText(40), submittedAt: NText(40), reviewedAt: NText(40) })

export const BookingSponsorshipDetail = z.object({
  version: z.literal(1),
  asOf: Iso,
  record: BookingSponsorshipRow,
  items: z
    .array(
      z.object({
        position: Count,
        label: Text(500),
        itemType: NText(40),
        amountMinor: NCents,
        coverageStatus: NText(40),
        dueDate: NDay,
        deliverables: z.array(Deliverable).max(200),
      }),
    )
    .max(200),
  otherDeliverables: z.array(Deliverable).max(200),
  invoices: z.array(Invoice).max(200),
  payments: z.array(Payment).max(200),
  agreement: z.object({
    status: NText(40),
    sentAt: NIso,
    signedAt: NIso,
    signers: z.array(z.object({ role: z.enum(['sponsor', 'bre']), status: Text(40), viewedAt: NIso, signedAt: NIso, sentAt: NIso })).max(50),
  }),
  messages: z.array(Message).max(200),
  activity: z.array(Activity).max(200),
})
export type BookingSponsorshipDetail = z.infer<typeof BookingSponsorshipDetail>

export const BookingChangeActor = z.object({
  kind: z.enum(['staff', 'customer', 'system']),
  sub: z.string().max(200).nullable(),
  channel: z.enum(['hub', 'mybre', 'booking']).nullable(),
  commandId: z.string().max(64).nullable(),
})
export type BookingChangeActor = z.infer<typeof BookingChangeActor>

export const BOOKING_CHANGE_ENTITIES = ['rental', 'sponsorship', 'contact', 'organization'] as const
export const BookingChange = z.object({
  seq: z.string().regex(/^\d{1,15}$/),
  entity: z.enum(BOOKING_CHANGE_ENTITIES),
  id: Id,
  kind: z.enum(['created', 'updated', 'deleted']),
  table: Text(80),
  at: NIso,
  // Who made the change (rentals and sponsorships plan, Phase 2): a change
  // made through a command or one of Booking's extracted domain actions
  // names its actor and channel; anything else is null (older Bookings
  // leave it out).
  actor: BookingChangeActor.nullable().optional(),
})
export type BookingChange = z.infer<typeof BookingChange>

export const BookingChangePage = z.object({
  version: z.literal(1),
  asOf: Iso,
  changes: z.array(BookingChange).max(BOOKING_CHANGES_MAX_LIMIT),
  nextCursor: z.string().regex(/^\d{1,15}$/),
  hasMore: z.boolean(),
  head: z.string().regex(/^\d{1,15}$/),
  oldest: z.string().regex(/^\d{1,15}$/).nullable(),
})
export type BookingChangePage = z.infer<typeof BookingChangePage>

// How Booking came to link a contact to the BRE account it names.
export const CONTACT_LINK_BASES = ['email_proof', 'auth_email_account', 'auth_email_placeholder', 'auth_phone_placeholder', 'auth_phone_account', 'unproven'] as const
export type ContactLinkBasis = (typeof CONTACT_LINK_BASES)[number]
// Proof Hub accepts on Booking's word: the account proved the email to
// Booking or to BRE Auth, or BRE Auth pre-staged a credential-less
// placeholder that only proof can claim (a claimed placeholder keeps its
// subject; a phone placeholder is merged into a real account only after an
// SMS code). BRE Auth answers a phone with whichever live account holds it,
// verified or not, so auth_phone_account needs Hub's own check of that
// account's phone; and a link with no evidence ('unproven') is never used.
export const PROVEN_CONTACT_BASES: ContactLinkBasis[] = ['email_proof', 'auth_email_account', 'auth_email_placeholder', 'auth_phone_placeholder']

export const BookingContact = z.object({
  sourceId: Id,
  firstName: NText(200),
  lastName: NText(200),
  email: NText(320),
  phone: NText(60),
  organization: z.object({ sourceId: Id, name: NText(500) }).nullable(),
  roles: z.array(z.enum(['renter', 'sponsor'])).max(2),
  rentals: Count,
  sponsorships: Count,
  account: z.object({ sub: Text(200).min(1), basis: z.enum(CONTACT_LINK_BASES) }).nullable(),
  // What BRE Auth answered for a contact Booking didn't link:
  // unverified_account (a real account typed the address but never proved
  // it: never linked), conflict, detached, pending, rejected.
  resolution: NText(40),
  createdAt: NIso,
  updatedAt: NIso,
  url: Url,
})
export type BookingContact = z.infer<typeof BookingContact>

export const BookingContactPage = z.object({
  version: z.literal(1),
  asOf: Iso,
  contacts: z.array(BookingContact).max(BOOKING_CONTACTS_MAX_LIMIT),
  nextCursor: z.string().max(64).nullable(),
  hasMore: z.boolean(),
})
export type BookingContactPage = z.infer<typeof BookingContactPage>

// --------------------------------------------- the contacts import rules

export type ContactLinkDecision =
  // Not a renter or sponsor: not imported.
  | { action: 'skip'; reason: 'not_renter_or_sponsor' }
  // Proven link and Hub has the person with that subject: link to them.
  | { action: 'link'; personId: string }
  // Proven link, but no Hub person holds the subject yet (people sync makes
  // them; dev never syncs production accounts). Nothing is made meanwhile,
  // so there is never a second person for the same account.
  | { action: 'await_person' }
  // No account, or one Hub won't link on Booking's word: a Hub person
  // without an account (ADR 0009 Phase 2), never matched on email or phone.
  | { action: 'import'; reason: 'no_account' | 'unproven_link' | 'phone_not_verified' }

// Decision 8 (owner, default accepted 2026-10-14) as Hub applies it to the
// contacts import: exact BRE Auth subject links only, on proof; never on an
// email or phone match; an unverified account (BRE Auth's
// unverified_account) is never linked, because Booking is never given its
// subject. `subjectPerson` is the active Hub person whose BRE Auth identity
// link is exactly the contact's subject, with the phones BRE Auth verified
// on that account (digits only).
export function contactLinkDecision(contact: Pick<BookingContact, 'roles' | 'account' | 'phone'>, subjectPerson: { personId: string; verifiedPhones: string[] } | null): ContactLinkDecision {
  if (contact.roles.length === 0) return { action: 'skip', reason: 'not_renter_or_sponsor' }
  const account = contact.account
  if (!account) return { action: 'import', reason: 'no_account' }
  if (account.basis === 'unproven') return { action: 'import', reason: 'unproven_link' }
  if (account.basis === 'auth_phone_account') {
    if (!subjectPerson) return { action: 'await_person' }
    const phone = contactPhoneKey(contact.phone)
    return phone && subjectPerson.verifiedPhones.some((p) => contactPhoneKey(p) === phone) ? { action: 'link', personId: subjectPerson.personId } : { action: 'import', reason: 'phone_not_verified' }
  }
  return subjectPerson ? { action: 'link', personId: subjectPerson.personId } : { action: 'await_person' }
}

// The last ten digits, so +1 (319) 555-0100 and 3195550100 compare equal.
export function contactPhoneKey(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : null
}

// ------------------------------------------------------- Hub's pipeline

// A stage is derived from Booking's fields every time, never stored: Hub
// has no rental status of its own (plan §5.1). `booked` (confirmed, the
// deposit in, the event more than FINAL_DETAILS_DAYS away) and `done` fill
// gaps between the plan's stages.
export const RENTAL_STAGES = ['inquiry', 'hold', 'quote', 'contract', 'deposit', 'booked', 'final_details', 'event', 'settlement', 'closeout', 'done', 'cancelled'] as const
export type RentalStage = (typeof RENTAL_STAGES)[number]
export const RENTAL_STAGE_LABELS: Record<RentalStage, string> = {
  inquiry: 'Inquiry',
  hold: 'Hold',
  quote: 'Quote',
  contract: 'Contract',
  deposit: 'Deposit',
  booked: 'Booked',
  final_details: 'Final details',
  event: 'Event',
  settlement: 'Settlement',
  closeout: 'Closeout',
  done: 'Done',
  cancelled: 'Canceled',
}
// The board's columns: work still to do.
export const RENTAL_BOARD_STAGES: RentalStage[] = ['inquiry', 'hold', 'quote', 'contract', 'deposit', 'booked', 'final_details', 'event', 'settlement', 'closeout']

export const SPONSORSHIP_STAGES = ['prospect', 'proposal', 'agreement', 'invoiced', 'paid', 'completed', 'cancelled'] as const
export type SponsorshipStage = (typeof SPONSORSHIP_STAGES)[number]
export const SPONSORSHIP_STAGE_LABELS: Record<SponsorshipStage, string> = {
  prospect: 'Prospect',
  proposal: 'Proposal',
  agreement: 'Agreement',
  invoiced: 'Invoiced',
  paid: 'Paid',
  completed: 'Completed',
  cancelled: 'Canceled',
}
export const SPONSORSHIP_BOARD_STAGES: SponsorshipStage[] = ['prospect', 'proposal', 'agreement', 'invoiced', 'paid']

// Days before an event when a confirmed rental moves into final details.
export const FINAL_DETAILS_DAYS = 30
// COI and the balance are wanted this many days before the event.
export const COI_DUE_DAYS = 14
export const BALANCE_DUE_DAYS = 14
export const HOLD_EXPIRY_WARN_DAYS = 7
export const RENEWAL_WARN_DAYS = 60

export function rentalStage(row: BookingRentalRow, today: string): RentalStage {
  const first = row.eventDate
  const last = row.lastDate ?? row.eventDate
  switch (row.status) {
    case 'cancelled':
      return 'cancelled'
    case 'inquiry':
      return row.holds.active > 0 ? 'hold' : 'inquiry'
    case 'estimate':
      return 'quote'
    case 'contract':
      return 'contract'
    case 'completed':
      return row.closeout === 'pending' || row.closeout === 'in_review' ? 'closeout' : 'done'
    case 'confirmed': {
      if (first && last && first <= today && today <= last) return 'event'
      if (last && last < today) return row.rentalType === 'ticketed' ? 'settlement' : 'closeout'
      const m = row.money
      if (m.collectedMinor === 0 && (m.billedMinor > 0 || (m.depositMinor ?? 0) > 0)) return 'deposit'
      if (first && daysBetween(today, first) <= FINAL_DETAILS_DAYS) return 'final_details'
      return 'booked'
    }
    default:
      // A status Hub doesn't know: shown at the start, with a warning.
      return 'inquiry'
  }
}

export function sponsorshipStage(row: BookingSponsorshipRow): SponsorshipStage {
  return (SPONSORSHIP_STAGES as readonly string[]).includes(row.status) ? (row.status as SponsorshipStage) : 'prospect'
}

// What needs someone, worked out from Booking's fields, most pressing
// first. The first is the record's next action. `money` items say only
// that something is owed; the amount is behind the money capabilities.
export type AttentionCode =
  | 'unknown_status'
  | 'overdue'
  | 'countersign'
  | 'contract_unsigned'
  | 'coi_needed'
  | 'insurance_failed'
  | 'deposit_unpaid'
  | 'balance_before_event'
  | 'hold_expiring'
  | 'documents_requested'
  | 'settle'
  | 'close_out'
  | 'no_owner'
  | 'taxed'
  | 'agreement_not_sent'
  | 'agreement_unsigned'
  | 'deliverables_to_review'
  | 'deliverables_overdue'
  | 'renewal_due'
export type Attention = { code: AttentionCode; text: string; tone: 'bad' | 'warn' | 'info' }

const KNOWN_RENTAL = new Set(['inquiry', 'estimate', 'contract', 'confirmed', 'completed', 'cancelled'])
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const shortDay = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })

export function rentalAttention(row: BookingRentalRow, today: string): Attention[] {
  const out: Attention[] = []
  const stage = rentalStage(row, today)
  const open = stage !== 'done' && stage !== 'cancelled'
  const days = row.eventDate ? daysBetween(today, row.eventDate) : null
  const p = row.paperwork
  if (!KNOWN_RENTAL.has(row.status)) out.push({ code: 'unknown_status', text: `Status “${row.status}” is new to Hub`, tone: 'warn' })
  if (row.money.overdueMinor > 0) out.push({ code: 'overdue', text: 'An invoice is overdue', tone: 'bad' })
  if (open && (p.contract === 'client_signed' || p.packetsToCountersign > 0)) out.push({ code: 'countersign', text: 'Signed by the renter; countersign for BRE', tone: 'warn' })
  if (row.status === 'contract' && p.contract !== 'client_signed' && p.contract !== 'fully_signed')
    out.push({ code: 'contract_unsigned', text: p.contract === 'sent' || p.packetsOpen > 0 ? 'Waiting for the renter to sign' : 'Contract not sent yet', tone: 'warn' })
  if (open && (row.status === 'contract' || row.status === 'confirmed') && (p.coi === 'required' || p.coi === 'rejected' || p.coi === 'expired') && days !== null && days <= COI_DUE_DAYS)
    out.push({ code: 'coi_needed', text: p.coi === 'required' ? 'Certificate of insurance not in yet' : `Certificate of insurance ${p.coi}`, tone: days < 0 ? 'bad' : 'warn' })
  if (p.insurance === 'purchase_failed' || p.policy === 'payment_failed') out.push({ code: 'insurance_failed', text: 'Event insurance purchase failed', tone: 'bad' })
  if (stage === 'deposit') out.push({ code: 'deposit_unpaid', text: 'Deposit not paid yet', tone: 'warn' })
  if (row.status === 'confirmed' && (row.money.balanceMinor ?? 0) > 0 && days !== null && days >= 0 && days <= BALANCE_DUE_DAYS && stage !== 'deposit')
    out.push({ code: 'balance_before_event', text: 'Balance due before the event', tone: 'warn' })
  if (row.holds.active > 0 && row.holds.expiresAt) {
    const expires = row.holds.expiresAt.slice(0, 10)
    if (expires <= addDays(today, HOLD_EXPIRY_WARN_DAYS)) out.push({ code: 'hold_expiring', text: `Hold expires ${shortDay(expires)}`, tone: expires < today ? 'bad' : 'warn' })
  }
  if (open && p.documentRequestsOpen > 0) out.push({ code: 'documents_requested', text: `${plural(p.documentRequestsOpen, 'document request')} open`, tone: 'info' })
  if (stage === 'settlement') out.push({ code: 'settle', text: 'Event over; settle it in Hub', tone: 'warn' })
  if (stage === 'closeout') out.push({ code: 'close_out', text: 'Event over; close it out', tone: 'info' })
  if (open && !row.owner) out.push({ code: 'no_owner', text: 'No owner', tone: 'info' })
  if (row.money.taxed) out.push({ code: 'taxed', text: 'Has a tax rate; BRE charges no sales tax', tone: 'warn' })
  return out
}

export function sponsorshipAttention(row: BookingSponsorshipRow, today: string): Attention[] {
  const out: Attention[] = []
  const open = row.status !== 'completed' && row.status !== 'cancelled'
  const a = row.agreement.status
  const d = row.deliverables
  if (!(SPONSORSHIP_STAGES as readonly string[]).includes(row.status)) out.push({ code: 'unknown_status', text: `Status “${row.status}” is new to Hub`, tone: 'warn' })
  if (row.money.overdueMinor > 0) out.push({ code: 'overdue', text: 'An invoice is overdue', tone: 'bad' })
  if (row.status === 'agreement' && (a === 'not_sent' || a === null)) out.push({ code: 'agreement_not_sent', text: 'Agreement not sent yet', tone: 'warn' })
  if (open && (a === 'sent' || a === 'partially_signed')) out.push({ code: 'agreement_unsigned', text: a === 'partially_signed' ? 'Agreement partly signed' : 'Waiting for the sponsor to sign', tone: 'warn' })
  if (open && d.awaitingReview > 0) out.push({ code: 'deliverables_to_review', text: `${plural(d.awaitingReview, 'submission')} to review`, tone: 'warn' })
  if (open && d.overdue > 0) out.push({ code: 'deliverables_overdue', text: `${plural(d.overdue, 'deliverable')} past due`, tone: 'bad' })
  const renewal = row.renewal.date
  if (renewal && row.renewal.status !== 'exercised' && row.renewal.status !== 'waived' && row.renewal.status !== 'expired' && renewal >= today && renewal <= addDays(today, RENEWAL_WARN_DAYS))
    out.push({ code: 'renewal_due', text: `Renewal ${shortDay(renewal)}`, tone: 'info' })
  if (open && !row.owner) out.push({ code: 'no_owner', text: 'No owner', tone: 'info' })
  return out
}

// ------------------------------------------------ what Hub's API answers

// Who may see Booking's money on these pages: the same rule as Event 360
// (finance.read or events.settle). rentals.money arrives with the money
// commands (plan Phase 5); until then, rentals.read and sponsorships.read
// see stages and paperwork but no amounts.
export type BookingMoneyVisibility = 'shown' | 'hidden'

// A pipeline entry: Booking's row (money removed when the viewer may not
// see it), plus what Hub derives and the Hub records it is tied to.
// A row as a viewer may see it: money is null when they may not see amounts.
export type VisibleRow<Row extends BookingRecordRow> = Omit<Row, 'money'> & { money: Row['money'] | null }

export type BookingPipelineEntry<Row extends BookingRecordRow, Stage extends string> = {
  row: VisibleRow<Row>
  stage: Stage
  attention: Attention[]
  // The Hub person and organization staff picked (ADR 0078), else the person
  // the contact is (by a proven BRE subject link or the import) and the
  // organization Booking's company became.
  person: { personId: string; displayName: string } | null
  organization: { organizationId: string; name: string } | null
  refreshedAt: string
  sourceAsOf: string
}
export type RentalPipelineEntry = BookingPipelineEntry<BookingRentalRow, RentalStage>
export type SponsorshipPipelineEntry = BookingPipelineEntry<BookingSponsorshipRow, SponsorshipStage>

export type BookingFeedFreshness = {
  // The switch: HUB_BOOKING_RECORDS on this Hub.
  enabled: boolean
  // When Hub last applied anything from Booking, and its last run.
  lastSuccessAt: string | null
  lastRunStatus: BookingSyncRunStatus | null
  lastError: string | null
  // Still reading every record for the first time.
  backfilling: boolean
}

export type RentalPipeline = { kind: 'rental'; today: string; money: BookingMoneyVisibility; entries: RentalPipelineEntry[]; feed: BookingFeedFreshness }
export type SponsorshipPipeline = { kind: 'sponsorship'; today: string; money: BookingMoneyVisibility; entries: SponsorshipPipelineEntry[]; feed: BookingFeedFreshness }

// Hub's own context for a record page: the Hub records it is tied to and
// what the change feed brought in.
export type BookingRecordContext = {
  kind: BookingRecordKind
  sourceId: string
  // 'picked': staff chose this person in Hub (ADR 0078).
  person: { personId: string; displayName: string; linkState: BookingContactLinkState | 'picked' } | null
  organization: { organizationId: string; name: string } | null
  // Booking rooms on Hub spaces (space_source_link, ADR 0023/0025).
  spaces: { roomId: string; spaceId: string; spaceName: string; facilityId: string | null; facilityName: string | null }[]
  // Hub events that link the Booking BRE event a sponsorship is for.
  events: { eventId: string; name: string }[]
  // actor: who Booking says made the change, when it says (name only for
  // viewers with people.read, from the Hub person holding that subject).
  changes: {
    seq: string
    kind: 'created' | 'updated' | 'deleted'
    table: string
    changedAt: string | null
    receivedAt: string
    actor: (BookingChangeActor & { name: string | null }) | null
  }[]
  projection: { refreshedAt: string; sourceAsOf: string; version: string; removedAt: string | null } | null
}

// A record page as a viewer may see it: without the money capabilities the
// quote, invoices and payments are null and the row's money is null.
export type VisibleRentalDetail = Omit<BookingRentalDetail, 'record' | 'quote' | 'invoices' | 'payments'> & {
  record: VisibleRow<BookingRentalRow>
  quote: BookingRentalDetail['quote'] | null
  invoices: BookingRentalDetail['invoices'] | null
  payments: BookingRentalDetail['payments'] | null
}
export type VisibleSponsorshipDetail = Omit<BookingSponsorshipDetail, 'record' | 'invoices' | 'payments'> & {
  record: VisibleRow<BookingSponsorshipRow>
  invoices: BookingSponsorshipDetail['invoices'] | null
  payments: BookingSponsorshipDetail['payments'] | null
}

export type RentalRecordView = {
  kind: 'rental'
  money: BookingMoneyVisibility
  today: string
  booking: { status: 'ok'; asOf: string; detail: VisibleRentalDetail; stage: RentalStage; attention: Attention[] } | { status: 'unavailable' | 'not_configured' | 'denied'; asOf: string; reason: string }
  // What Hub last held, when Booking can't be asked.
  lastKnown: RentalPipelineEntry | null
  context: BookingRecordContext
}
export type SponsorshipRecordView = {
  kind: 'sponsorship'
  money: BookingMoneyVisibility
  today: string
  booking: { status: 'ok'; asOf: string; detail: VisibleSponsorshipDetail; stage: SponsorshipStage; attention: Attention[] } | { status: 'unavailable' | 'not_configured' | 'denied'; asOf: string; reason: string }
  lastKnown: SponsorshipPipelineEntry | null
  context: BookingRecordContext
}

// On a person, organization or event page.
export type BookingRecordLinks = {
  money: BookingMoneyVisibility
  today: string
  rentals: RentalPipelineEntry[] | null
  sponsorships: SponsorshipPipelineEntry[] | null
  feed: BookingFeedFreshness
  // For a person: their Booking contacts and how each is tied to them.
  contacts?: { sourceId: string; name: string | null; linkState: BookingContactLinkState; basis: ContactLinkBasis | null; url: string | null }[]
}

// ----------------------------------------------------- the sync, in Hub

// The cron signs its core calls as this subject with no person; core lets
// it do nothing but run the Booking sync.
export const BOOKING_RECORDS_SYSTEM_SUB = 'system:booking-records'

export type BookingSyncPart = 'feed' | 'contacts'
export type BookingSyncTrigger = 'scheduled' | 'refresh'
export type BookingSyncRunStatus = 'running' | 'ok' | 'partial' | 'error' | 'not_connected'

// How a Booking contact is tied to Hub: by the exact BRE subject (subject),
// as a Hub person made by the import (imported), waiting for people sync to
// make the subject's person (awaiting_person), or not imported (a contact
// who neither rents nor sponsors).
export type BookingContactLinkState = 'subject' | 'imported' | 'awaiting_person' | 'not_imported'

export type BookingSyncCounts = {
  // feed
  rentals: number
  sponsorships: number
  removed: number
  changes: number
  // contacts
  contactsSeen: number
  linked: number
  imported: number
  relinked: number
  awaitingPerson: number
  notImported: number
  missing: number
}
export const emptyBookingSyncCounts = (): BookingSyncCounts => ({
  rentals: 0,
  sponsorships: 0,
  removed: 0,
  changes: 0,
  contactsSeen: 0,
  linked: 0,
  imported: 0,
  relinked: 0,
  awaitingPerson: 0,
  notImported: 0,
  missing: 0,
})

export type BookingSyncRun = {
  id: string
  part: BookingSyncPart
  trigger: BookingSyncTrigger
  startedAt: string
  finishedAt: string | null
  status: BookingSyncRunStatus
  pages: number
  counts: BookingSyncCounts
  error: string | null
}

export type BookingSyncStatus = {
  enabled: boolean
  connection: { connected: boolean; reason: string | null }
  feed: {
    cursor: string | null
    backfill: { kind: BookingRecordKind | null; cursor: string | null } | null
    lastFullReadAt: string | null
    lastSuccessAt: string | null
    lastRun: BookingSyncRun | null
    rentals: number
    sponsorships: number
    removed: number
  }
  contacts: {
    lastFullReadAt: string | null
    lastSuccessAt: string | null
    lastRun: BookingSyncRun | null
    byState: Record<BookingContactLinkState, number>
    unproven: number
  }
}

export type BookingSyncPartResult =
  | { part: BookingSyncPart; status: 'not_connected' | 'busy' | 'off'; message: string }
  | { part: BookingSyncPart; status: 'ok' | 'partial' | 'error'; runId: string; pages: number; counts: BookingSyncCounts; message: string | null }
export type BookingSyncResult = { status: 'ran' | 'off'; parts: BookingSyncPartResult[] }

// A full re-read of every rental and sponsorship (and every contact) at
// least this often: Booking's room or owner names, which its feed doesn't
// carry, and anything Hub missed, catch up within a day.
export const BOOKING_FULL_READ_EVERY_MS = 24 * 60 * 60 * 1000

// The money a viewer may not see, taken out. Stage and attention are worked
// out before this, from the whole row.
export function withoutMoney<Row extends BookingRecordRow>(row: Row): VisibleRow<Row> {
  return { ...row, money: null }
}
export function rentalDetailWithoutMoney(d: BookingRentalDetail): VisibleRentalDetail {
  return { ...d, record: withoutMoney(d.record), quote: null, invoices: null, payments: null, insurance: { ...d.insurance, policies: d.insurance.policies.map((p) => ({ ...p, coverageMinor: null, premiumMinor: null })) } }
}
export function sponsorshipDetailWithoutMoney(d: BookingSponsorshipDetail): VisibleSponsorshipDetail {
  return { ...d, record: withoutMoney(d.record), invoices: null, payments: null, items: d.items.map((i) => ({ ...i, amountMinor: null })) }
}
